import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.svg';
import { launchSuiteApp } from '../utils/launchSuiteApp';

/**
 * WorkspaceHome — the Central `/home` route.
 *
 * This is the Central-hosted landing / recovery / launcher shell served at
 * `<central>/home`. It is NOT the Home suite app (`appId: 'home'`, served at
 * its own `SUITE_APP_HOME_URL`, e.g. `<home>/home`) — the two are distinct.
 * This shell lists the workspace's suite apps and lets the user launch into
 * them; the actual destination experiences are the suite apps themselves.
 *
 * Normal users finishing onboarding (or logging back in) land on this route,
 * not on the admin control plane at `/` (which is admin-gated and 403s for
 * non-admins).
 *
 * Data source is the session-authed `GET /api/workspace/current`. The
 * endpoint's `status` drives what this route does:
 *  - no-workspace          → redirect to the workspace picker.
 *  - onboarding-incomplete → redirect to resume onboarding.
 *  - provisioned           → render the workspace's launchable suite apps.
 *  - empty                 → friendly "still settling" state with refresh.
 *
 * On a hard fetch failure the route shows a recoverable `degraded` state.
 * A 401 means there is no session at all → redirect to login. None of these
 * paths dead-end the user.
 */

interface HomeApp {
  appId: string;
  displayName: string;
  /**
   * Phase 2.x-c — Application Registry v2 metadata, mode-resolved on
   * the backend (business vs personal per workspace.entryType). Optional
   * so the tile degrades gracefully when the registry lacks copy or the
   * backend predates PR-A.
   */
  subtitle?: string;
  description?: string;
  order: number;
  /** External suite-app launch URL, or null when not resolvable. */
  launchUrl: string | null;
  /** True when the app has a usable launchUrl. */
  launchable: boolean;
  /** H-2A: true → launch via the OIDC flow; false/absent → bare link. */
  oidcLaunch?: boolean;
}

interface DefaultApp {
  appId: string;
  displayName: string;
  launchUrl: string | null;
  launchable: boolean;
  oidcLaunch?: boolean;
}

type HomeStatus = 'loading' | 'provisioned' | 'empty' | 'degraded' | 'closed';

export function WorkspaceHomePage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<HomeStatus>('loading');
  const [workspaceName, setWorkspaceName] = useState<string>('your workspace');
  const [apps, setApps] = useState<HomeApp[]>([]);
  const [defaultApp, setDefaultApp] = useState<DefaultApp | null>(null);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await fetch('/api/workspace/current', { credentials: 'include' });

      // No session at all — send the user to sign in.
      if (res.status === 401) {
        navigate('/auth/login');
        return;
      }
      if (!res.ok) {
        setStatus('degraded');
        return;
      }

      const data = await res.json();
      if (!data?.ok) {
        setStatus('degraded');
        return;
      }

      switch (data.status) {
        case 'no-workspace':
          // Session has no workspace bound — route to the picker, never
          // to the admin control plane.
          navigate('/auth/workspace');
          return;
        case 'closed-workspace':
          // The bound workspace is closed — show a terminal "unavailable"
          // screen with a path to pick an active workspace. Deliberately
          // not an auto-redirect, to avoid re-selecting the same closed
          // workspace and looping.
          setStatus('closed');
          return;
        case 'onboarding-incomplete': {
          // The bound workspace exists but onboarding is unfinished — resume
          // THAT workspace via /start/{mode} (StartEntry resumes the bound
          // workspace at its current step). Do NOT route to /onboarding/new —
          // that is the create-a-NEW-workspace chooser, not a resume path.
          const mode = data.workspace?.mode === 'personal' ? 'personal' : 'business';
          navigate(`/start/${mode}`);
          return;
        }
        case 'provisioned':
        case 'empty': {
          const resolvedApps: HomeApp[] = Array.isArray(data.apps) ? data.apps : [];
          setWorkspaceName(data.workspace?.name?.trim() || 'your workspace');
          setApps(resolvedApps);
          setDefaultApp(data.defaultApp ?? null);
          setStatus(data.status);
          return;
        }
        default:
          setStatus('degraded');
      }
    } catch {
      setStatus('degraded');
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={logo} alt="LevelUp" className="h-8" />
          <Link to="/settings" className="text-sm text-blue-600 hover:text-blue-800">
            Workspace settings
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {status === 'loading' && (
          <div className="flex flex-col items-center justify-center py-24" role="status">
            <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
            <p className="text-gray-500 mt-4">Loading your workspace…</p>
          </div>
        )}

        {status === 'provisioned' && (
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Welcome to {workspaceName}
            </h1>
            <p className="text-gray-500 mt-1">
              Your workspace is ready. Open an app to get started.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
              {apps.map((app) => {
                const isDefault = defaultApp?.appId === app.appId;
                const canLaunch = app.launchable && !!app.launchUrl;

                // Phase 2.x-c — the tile carries the Application
                // Registry v2 subtitle beneath the displayName so users
                // see the enduring customer value ("Customer
                // relationships") without opening the app. Description
                // is available on the payload but not surfaced on the
                // launcher today; a future PR may add a hover state.
                const header = (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-medium text-gray-900">{app.displayName}</h3>
                      {app.subtitle && (
                        <p
                          className="text-xs text-gray-500 mt-0.5"
                          data-testid={`tile-subtitle-${app.appId}`}
                        >
                          {app.subtitle}
                        </p>
                      )}
                    </div>
                    {isDefault && (
                      <span className="text-xs font-medium text-blue-600 shrink-0">
                        Recommended
                      </span>
                    )}
                  </div>
                );

                // Launchable → a real link into the suite app. The default
                // app is styled to feel primary. When the app is
                // OIDC-handoff enabled (oidcLaunch), the click is routed
                // through the canonical launch-intent flow; otherwise the
                // bare href is used. The href stays the bare URL either
                // way — a no-JS fallback — and is never inspected.
                if (canLaunch) {
                  const launchUrl = app.launchUrl as string;
                  return (
                    <a
                      key={app.appId}
                      href={launchUrl}
                      onClick={(e) => {
                        if (!app.oidcLaunch) return;
                        e.preventDefault();
                        void launchSuiteApp(app.appId, {
                          surface: 'home-launcher',
                          fallbackUrl: launchUrl,
                        });
                      }}
                      className={`block bg-white border rounded-lg p-5 transition-all hover:shadow-sm ${
                        isDefault
                          ? 'border-blue-500 ring-2 ring-blue-500'
                          : 'border-gray-200 hover:border-blue-500'
                      }`}
                    >
                      {header}
                      <p
                        className={`mt-3 text-sm font-medium ${
                          isDefault ? 'text-white bg-blue-600 rounded-md px-3 py-1.5 inline-block' : 'text-blue-600'
                        }`}
                      >
                        Open {app.displayName}
                        {!isDefault && ' →'}
                      </p>
                    </a>
                  );
                }

                // Not launchable → disabled, non-interactive card. Never a
                // dead link; the page still renders cleanly.
                return (
                  <div
                    key={app.appId}
                    aria-disabled="true"
                    className={`bg-white border rounded-lg p-5 opacity-60 cursor-not-allowed ${
                      isDefault ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'
                    }`}
                  >
                    {header}
                    <p className="mt-3 text-sm text-gray-400">Currently unavailable</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-10">
              <h2 className="text-sm font-medium text-gray-700">Next steps</h2>
              <ul className="mt-2 space-y-1">
                <li>
                  <Link to="/settings" className="text-sm text-blue-600 hover:text-blue-800">
                    Review your workspace settings
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        )}

        {status === 'empty' && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center max-w-md mx-auto">
            <h1 className="text-lg font-semibold text-gray-900">
              {workspaceName} is set up
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              We're still preparing your apps. This usually only takes a moment.
            </p>
            <button
              type="button"
              onClick={() => load()}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        )}

        {status === 'closed' && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center max-w-md mx-auto">
            <h1 className="text-lg font-semibold text-gray-900">
              This workspace is closed
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              The workspace you were in has been closed and is no longer
              available. Choose another workspace to continue.
            </p>
            <Link
              to="/auth/workspace"
              className="mt-6 inline-block px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors"
            >
              Choose a workspace
            </Link>
          </div>
        )}

        {status === 'degraded' && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center max-w-md mx-auto">
            <h1 className="text-lg font-semibold text-gray-900">
              We couldn't load your workspace
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              Something went wrong reaching your workspace. Your data is safe —
              please try again.
            </p>
            <button
              type="button"
              onClick={() => load()}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors"
            >
              Try again
            </button>
            <p className="mt-4 text-sm text-gray-500">
              <Link to="/auth/login" className="text-blue-600 hover:text-blue-800">
                Sign in
              </Link>
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
